import React from 'react';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import clsx from 'clsx';
import {
  FormCategoryRuleDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import ToolTip from 'core/components/Tooltip';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import {
  ALL_FORM_FIELD_ASSIGNEES,
  getAllowedAssignees,
  getAssigneeLabel,
  getNextAssigneeSelection,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formAssigneeSelection';

type Props = {
  categoryRule?: FormCategoryRuleDTO | null;
  selectedAssignees: FormFieldAssignee[];
  onSelectionChange: (nextSelection: FormFieldAssignee[]) => void;
};

const assigneeHelpText = (
  'Choose one assignee to apply to every field on this form. '
  + 'Choose both to set the assignee individually for each field.'
);

type AssigneeCheckboxProps = {
  assignee: FormFieldAssignee;
  isSelected: boolean;
  isDisabled: boolean;
  disabledTooltip?: string;
  onToggle: () => void;
};

function AssigneeCheckbox({
  assignee,
  isSelected,
  isDisabled,
  disabledTooltip,
  onToggle,
}: AssigneeCheckboxProps) {
  const isPatient = assignee === FormFieldAssignee.Patient;
  const label = getAssigneeLabel(assignee);

  const checkboxControlClasses = clsx(
    isPatient && '[&.Mui-checked]:!text-secondary [&.Mui-disabled.Mui-checked]:!text-secondary',
    !isPatient && '[&.Mui-checked]:!text-violet-500 [&.Mui-disabled.Mui-checked]:!text-violet-500',
  );

  const checkbox = (
    <Checkbox
      id={`form-assignee-${assignee}`}
      label={label}
      checked={isSelected}
      disabled={isDisabled}
      color={isPatient ? 'secondary' : undefined}
      containerClassName="!m-0"
      classes={{
        checkbox: checkboxControlClasses,
        label: clsx(
          '!text-xs !font-medium',
          isPatient ? '!text-secondary' : '!text-violet-700',
        ),
      }}
      onChange={() => {
        onToggle();
      }}
    />
  );

  const checkboxContent = (
    <span className={clsx('inline-flex', isDisabled && 'opacity-70')}>
      {checkbox}
    </span>
  );

  return isDisabled && disabledTooltip ? (
    <ToolTip title={disabledTooltip}>
      {checkboxContent}
    </ToolTip>
  ) : checkboxContent;
}

function getDisabledTooltip(
  assignee: FormFieldAssignee,
  isAllowed: boolean,
  isLockedSelection: boolean,
  allowsAssigneeSelection: boolean,
): string | undefined {
  if (!isAllowed) {
    return 'This assignee is not allowed for the selected form category.';
  }

  if (isLockedSelection && !allowsAssigneeSelection) {
    return `This form category only allows ${getAssigneeLabel(assignee)} fields.`;
  }

  if (isLockedSelection) {
    return 'At least one assignee must remain selected.';
  }

  return undefined;
}

export default function FormAssigneeSelector({
  categoryRule,
  selectedAssignees,
  onSelectionChange,
}: Props) {
  const allowedAssignees = getAllowedAssignees(categoryRule);
  const allowsAssigneeSelection = allowedAssignees.length > 1;

  return (
    <div
      className={clsx(
        'inline-flex flex-wrap items-center gap-3 rounded-xl',
        'border border-gray-200 bg-gray-50 px-3 py-2 text-xs',
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-semibold text-gray-700">Who fills this form?</span>
      </div>

      <div
        className={clsx(
          'inline-flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-1',
        )}
        role="group"
        aria-label="Fields completed by"
      >
        {ALL_FORM_FIELD_ASSIGNEES.map((assignee) => {
          const isAllowed = allowedAssignees.includes(assignee);
          const isSelected = selectedAssignees.includes(assignee);
          const isLockedSelection = isSelected && selectedAssignees.length === 1;
          const isDisabled = !isAllowed || isLockedSelection;
          const disabledTooltip = getDisabledTooltip(
            assignee,
            isAllowed,
            isLockedSelection,
            allowsAssigneeSelection,
          );

          return (
            <AssigneeCheckbox
              key={assignee}
              assignee={assignee}
              isSelected={isSelected}
              isDisabled={isDisabled}
              disabledTooltip={disabledTooltip}
              onToggle={() => {
                const nextSelection = getNextAssigneeSelection(
                  selectedAssignees,
                  assignee,
                  categoryRule,
                );

                if (nextSelection === null) {
                  return;
                }

                onSelectionChange(nextSelection);
              }}
            />
          );
        })}
      </div>

      {allowsAssigneeSelection ? (
        <ToolTip title={assigneeHelpText}>
          <button
            type="button"
            className="inline-flex shrink-0 text-gray-400 transition-colors hover:text-gray-600"
            aria-label="How field assignee selection works"
          >
            <HelpOutlineRoundedIcon className="!h-4 !w-4" />
          </button>
        </ToolTip>
      ) : null}
    </div>
  );
}
